import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Instagram, MapPin, Send, Check, Facebook, Linkedin, Link2 } from "lucide-react";
import api from "@/lib/api";

const STUDIO_SOCIALS = [
    { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/silvertree_designstudio/" },
    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/StudioSilverTree1176" },
    { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/silvertree-communication-design-studio" },
    { icon: Link2, label: "Linktree", href: "https://linktr.ee/silvertree1176" },
];

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
    const [status, setStatus] = useState({ state: "idle", msg: "" });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ state: "loading", msg: "" });
        try {
            const { data } = await api.post("/contact", form);
            setStatus({ state: "success", msg: data.message });
            setForm({ name: "", email: "", company: "", interest: "", message: "" });
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
            setStatus({ state: "error", msg });
        }
    };

    return (
        <main data-testid="contact-page" className="bg-[var(--st-cream)]">
            <section className="pt-36 lg:pt-44 pb-12">
                <div className="st-container">
                    <p className="eyebrow text-[var(--st-gold-deep)] mb-6">Let's begin</p>
                    <h1 className="font-serif leading-[0.92] tracking-tighter max-w-5xl" style={{ fontSize: "clamp(3rem, 7vw, 7rem)" }}>
                        Tell us what<br />
                        <span className="italic text-[var(--st-gold)]">you're building.</span>
                    </h1>
                </div>
            </section>

            <section className="py-12 lg:py-20">
                <div className="st-container grid grid-cols-1 lg:grid-cols-12 gap-14">
                    {/* Form */}
                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        onSubmit={submit}
                        className="lg:col-span-7"
                        data-testid="contact-form"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                            <div>
                                <label className="eyebrow text-[var(--st-ink-soft)] block mb-1">Name</label>
                                <input data-testid="contact-name" required className="st-input" value={form.name} onChange={update("name")} placeholder="Your name" />
                            </div>
                            <div>
                                <label className="eyebrow text-[var(--st-ink-soft)] block mb-1">Email</label>
                                <input data-testid="contact-email" required type="email" className="st-input" value={form.email} onChange={update("email")} placeholder="you@example.com" />
                            </div>
                            <div>
                                <label className="eyebrow text-[var(--st-ink-soft)] block mb-1 mt-6">Company (optional)</label>
                                <input data-testid="contact-company" className="st-input" value={form.company} onChange={update("company")} placeholder="Your brand" />
                            </div>
                            <div>
                                <label className="eyebrow text-[var(--st-ink-soft)] block mb-1 mt-6">I'm interested in</label>
                                <select data-testid="contact-interest" className="st-input bg-transparent" value={form.interest} onChange={update("interest")}>
                                    <option value="">Pick a room</option>
                                    <option value="design">Design</option>
                                    <option value="consult">Consult</option>
                                    <option value="learn">Learn</option>
                                    <option value="other">Something else</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-8">
                            <label className="eyebrow text-[var(--st-ink-soft)] block mb-1">Tell us about your project</label>
                            <textarea data-testid="contact-message" required className="st-input" rows={6} value={form.message} onChange={update("message")} placeholder="What's on your mind? Even rough thoughts are welcome." />
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
                            <button
                                type="submit"
                                disabled={status.state === "loading"}
                                data-testid="contact-submit"
                                className="btn-primary disabled:opacity-60"
                            >
                                {status.state === "loading" ? "Sending…" : status.state === "success" ? <>Sent <Check size={16} /></> : <>Send message <Send size={14} /></>}
                            </button>
                            {status.msg && (
                                <p data-testid="contact-status" className={`text-sm ${status.state === "success" ? "text-[var(--st-green)]" : "text-red-700"}`}>
                                    {status.msg}
                                </p>
                            )}
                        </div>
                    </motion.form>

                    {/* Side info */}
                    <aside className="lg:col-span-4 lg:col-start-9 space-y-10">
                        <div>
                            <p className="eyebrow text-[var(--st-gold-deep)] mb-3">Or write to us directly</p>
                            <a href="mailto:sugandhi@thesilver-tree.com" className="flex items-center gap-2 link-underline font-serif text-2xl">
                                <Mail size={18} /> sugandhi@thesilver-tree.com
                            </a>
                            <div className="mt-6">
                                <p className="eyebrow text-[var(--st-gold-deep)] mb-3">Follow the studio</p>
                                <div className="flex items-center gap-3">
                                    {STUDIO_SOCIALS.map((s) => (
                                        <a
                                            key={s.label}
                                            href={s.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`contact-social-${s.label.toLowerCase()}`}
                                            aria-label={s.label}
                                            className="w-11 h-11 border border-[var(--st-line)] flex items-center justify-center text-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-[var(--st-cream)] transition-colors"
                                        >
                                            <s.icon size={16} />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center gap-2 eyebrow text-[var(--st-gold-deep)] mb-2"><MapPin size={14}/> Gurgaon</div>
                                <p className="text-[var(--st-ink-soft)] leading-relaxed">Sector 48, Sohna Road<br/>Gurgaon, Haryana, India</p>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 eyebrow text-[var(--st-gold-deep)] mb-2"><MapPin size={14}/> Kuala Lumpur</div>
                                <p className="text-[var(--st-ink-soft)] leading-relaxed">Mont Kiara, Jalan Dutamas<br/>Kuala Lumpur, Malaysia</p>
                            </div>
                        </div>

                        <div className="border-t border-[var(--st-line)] pt-6">
                            <p className="eyebrow text-[var(--st-gold-deep)] mb-2">Response time</p>
                            <p className="text-[var(--st-ink-soft)] leading-relaxed">We reply to every brief within one working day. Promise.</p>
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
};

export default Contact;
